import React, { useState, useEffect } from 'react';
import { timeLogsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, addDays, subDays, eachDayOfInterval, differenceInDays } from 'date-fns';
import { toast } from 'sonner';
import {
  Clock,
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  Users,
  Calendar,
  CalendarRange
} from 'lucide-react';
import Layout from '../components/Layout';
import { TranslatableSpan } from '../components/TranslatableText';

const TimeReportPage = () => {
  // Period type: weekly, biweekly, custom
  const [periodType, setPeriodType] = useState('weekly');
  
  // For weekly/biweekly navigation
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  
  // For custom date range
  const [customStartDate, setCustomStartDate] = useState(format(subDays(new Date(), 13), 'yyyy-MM-dd'));
  const [customEndDate, setCustomEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState(null);

  // Calculate date range based on period type
  const getDateRange = () => {
    if (periodType === 'weekly') {
      const start = currentWeekStart;
      const end = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
      return { start, end };
    } else if (periodType === 'biweekly') {
      const start = currentWeekStart;
      const end = addDays(endOfWeek(addWeeks(currentWeekStart, 1), { weekStartsOn: 1 }), 0);
      return { start, end };
    } else {
      // custom
      return { 
        start: new Date(customStartDate), 
        end: new Date(customEndDate) 
      };
    }
  };

  const { start: rangeStart, end: rangeEnd } = getDateRange();
  const rangeDays = eachDayOfInterval({ start: rangeStart, end: rangeEnd });

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWeekStart, periodType, customStartDate, customEndDate]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      const startDate = format(start, 'yyyy-MM-dd');
      const endDate = format(end, 'yyyy-MM-dd');
      const response = await timeLogsAPI.getWeeklyReport(startDate, endDate);
      setReport(response.data);
    } catch (err) {
      console.error('Failed to fetch report:', err);
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const goToPrevious = () => {
    if (periodType === 'weekly') {
      setCurrentWeekStart(prev => subWeeks(prev, 1));
    } else if (periodType === 'biweekly') {
      setCurrentWeekStart(prev => subWeeks(prev, 2));
    }
  };

  const goToNext = () => {
    if (periodType === 'weekly') {
      setCurrentWeekStart(prev => addWeeks(prev, 1));
    } else if (periodType === 'biweekly') {
      setCurrentWeekStart(prev => addWeeks(prev, 2));
    }
  };

  const goToCurrent = () => {
    setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
  };

  const handlePeriodChange = (value) => {
    setPeriodType(value);
    if (value !== 'custom') {
      setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
    }
  };

  const exportToCSV = () => {
    if (!report || !report.employees.length) {
      toast.error('No data to export');
      return;
    }

    // Create CSV headers - limit columns for large date ranges
    const maxDays = 14;
    const displayDays = rangeDays.length > maxDays ? rangeDays.slice(0, maxDays) : rangeDays;
    const headers = ['Employee', ...displayDays.map(d => format(d, 'EEE MM/dd')), 'Total Hours'];
    
    // Create rows
    const rows = report.employees.map(emp => {
      const dailyHours = displayDays.map(day => {
        const dayStr = format(day, 'yyyy-MM-dd');
        const hours = emp.daily_hours?.[dayStr] || 0;
        return hours > 0 ? hours.toFixed(1) : '-';
      });
      return [emp.user_name, ...dailyHours, emp.total_hours.toFixed(1)];
    });

    // Add totals row
    const dailyTotals = displayDays.map(day => {
      const dayStr = format(day, 'yyyy-MM-dd');
      const total = report.employees.reduce((sum, emp) => {
        return sum + (emp.daily_hours?.[dayStr] || 0);
      }, 0);
      return total > 0 ? total.toFixed(1) : '-';
    });
    rows.push(['TOTAL', ...dailyTotals, report.total_hours.toFixed(1)]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `time-report-summary-${format(rangeStart, 'yyyy-MM-dd')}-to-${format(rangeEnd, 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Summary exported to CSV');
  };

  const exportDetailedCSV = () => {
    if (!report || !report.employees.length) {
      toast.error('No data to export');
      return;
    }

    // Detailed export with all entries
    const headers = ['Employee', 'Date', 'Start Time', 'End Time', 'Hours', 'Work Order', 'Category', 'Description'];
    
    const rows = [];
    report.employees.forEach(emp => {
      emp.entries.forEach(entry => {
        rows.push([
          emp.user_name,
          entry.date,
          entry.start_time || '',
          entry.end_time || '',
          entry.hours.toFixed(1),
          entry.work_order_number || 'General',
          entry.category_name || '',
          `"${(entry.description || '').replace(/"/g, '""')}"`
        ]);
      });
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `time-report-detailed-${format(rangeStart, 'yyyy-MM-dd')}-to-${format(rangeEnd, 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Detailed report exported to CSV');
  };

  // Get display columns - limit to reasonable amount for UI
  const getDisplayDays = () => {
    if (rangeDays.length <= 14) return rangeDays;
    // For larger ranges, show first week and last week
    return rangeDays;
  };

  const displayDays = getDisplayDays();

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase tracking-tight text-white">
              Time Report
            </h1>
            <p className="text-slate-400 mt-1">Employee work hours summary</p>
          </div>
          
          <div className="flex gap-2 flex-wrap">
            <Button onClick={exportToCSV} className="btn-secondary flex items-center gap-2" data-testid="export-summary-btn">
              <Download className="w-4 h-4" />
              Export Summary
            </Button>
            <Button onClick={exportDetailedCSV} className="btn-primary flex items-center gap-2" data-testid="export-detailed-btn">
              <Download className="w-4 h-4" />
              Export Detailed
            </Button>
          </div>
        </div>

        {/* Period Selection */}
        <div className="card-industrial">
          <div className="flex flex-col gap-4">
            {/* Period Type Selector */}
            <div className="flex items-center gap-3">
              <CalendarRange className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <span className="text-slate-400 text-sm uppercase tracking-wider">Period:</span>
              <Select value={periodType} onValueChange={handlePeriodChange}>
                <SelectTrigger className="w-32 sm:w-40 bg-slate-800 border-slate-700 text-white" data-testid="period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Biweekly</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Date Navigation / Custom Dates */}
            {periodType !== 'custom' ? (
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <div className="flex items-center justify-between w-full sm:w-auto gap-2 bg-slate-800/50 rounded-sm p-2">
                  <Button variant="ghost" onClick={goToPrevious} className="text-slate-400 hover:text-white p-2 h-auto">
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  
                  <div className="flex items-center gap-2 text-center">
                    <Calendar className="w-4 h-4 text-cyan-400" />
                    <span className="font-bold text-sm sm:text-lg text-white">
                      {format(rangeStart, 'MMM d')} - {format(rangeEnd, 'MMM d, yyyy')}
                    </span>
                  </div>
                  
                  <Button variant="ghost" onClick={goToNext} className="text-slate-400 hover:text-white p-2 h-auto">
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>
                
                <Button variant="outline" onClick={goToCurrent} className="btn-secondary text-sm w-full sm:w-auto">
                  {periodType === 'weekly' ? 'This Week' : 'Current'}
                </Button>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <span className="text-slate-400 text-sm w-12">From:</span>
                  <Input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="bg-slate-800 border-slate-700 text-white flex-1 sm:w-40"
                    data-testid="custom-start-date"
                  />
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <span className="text-slate-400 text-sm w-12">To:</span>
                  <Input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="bg-slate-800 border-slate-700 text-white flex-1 sm:w-40"
                    data-testid="custom-end-date"
                  />
                </div>
                <span className="text-slate-400 text-sm bg-slate-800/50 px-3 py-1 rounded">
                  {differenceInDays(new Date(customEndDate), new Date(customStartDate)) + 1} days
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Summary Stats */}
        <div className="card-industrial">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-slate-400" />
                <span className="text-slate-400">{report?.employees?.length || 0} Employees</span>
              </div>
              <div className="text-slate-400">
                Total Hours: <span className="text-amber-400 font-bold text-xl">{report?.total_hours?.toFixed(1) || 0} hrs</span>
              </div>
            </div>
          </div>
        </div>

        {/* Report Table */}
        {loading ? (
          <div className="card-industrial flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
          </div>
        ) : report?.employees?.length > 0 ? (
          <div className="card-industrial overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-3 px-4 text-slate-400 uppercase text-xs tracking-wider font-semibold">
                    Employee
                  </th>
                  {displayDays.map((day) => (
                    <th key={format(day, 'yyyy-MM-dd')} className="text-center py-3 px-2 text-slate-400 uppercase text-xs tracking-wider font-semibold">
                      <div>{format(day, 'EEE')}</div>
                      <div className="text-slate-500">{format(day, 'M/d')}</div>
                    </th>
                  ))}
                  <th className="text-center py-3 px-4 text-amber-400 uppercase text-xs tracking-wider font-semibold">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {report.employees.map((emp) => (
                  <React.Fragment key={emp.user_id}>
                    <tr 
                      className="border-b border-slate-700/50 hover:bg-slate-800/50 cursor-pointer transition-colors"
                      onClick={() => setExpandedUser(expandedUser === emp.user_id ? null : emp.user_id)}
                    >
                      <td className="py-3 px-4">
                        <div className="text-white font-medium">{emp.user_name}</div>
                        <div className="text-slate-500 text-xs">{emp.entries?.length || 0} entries</div>
                      </td>
                      {displayDays.map((day) => {
                        const dayStr = format(day, 'yyyy-MM-dd');
                        const hours = emp.daily_hours?.[dayStr] || 0;
                        return (
                          <td key={dayStr} className="text-center py-3 px-2">
                            {hours > 0 ? (
                              <span className={`font-mono ${hours >= 8 ? 'text-green-400' : hours >= 4 ? 'text-cyan-400' : 'text-slate-300'}`}>
                                {hours.toFixed(1)}
                              </span>
                            ) : (
                              <span className="text-slate-600">-</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="text-center py-3 px-4">
                        <span className="text-amber-400 font-bold">{emp.total_hours.toFixed(1)}</span>
                      </td>
                    </tr>
                    
                    {/* Expanded entries */}
                    {expandedUser === emp.user_id && emp.entries?.length > 0 && (
                      <tr>
                        <td colSpan={displayDays.length + 2} className="bg-slate-800/30 p-4">
                          <div className="space-y-2">
                            <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
                              Time Entries for {emp.user_name}
                            </h4>
                            {emp.entries.map((entry, idx) => (
                              <div key={entry.id || `${entry.date}-${entry.start_time}-${idx}`} className="flex items-center gap-4 p-2 bg-slate-900/50 rounded-sm text-sm">
                                <span className="text-cyan-400 font-mono w-24">
                                  {entry.date ? format(new Date(entry.date + 'T00:00:00'), 'MM-dd-yyyy') : '-'}
                                </span>
                                <span className="text-slate-400 w-32">
                                  {entry.start_time && entry.end_time 
                                    ? `${entry.start_time} - ${entry.end_time}` 
                                    : '-'}
                                </span>
                                <span className="text-amber-400 font-bold w-16">{entry.hours.toFixed(1)} hrs</span>
                                <span className="text-purple-400 w-28">{entry.work_order_number || 'General'}</span>
                                <span className="text-slate-300 flex-1 truncate"><TranslatableSpan text={entry.description} /></span>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                
                {/* Totals Row */}
                <tr className="bg-slate-800/50 font-bold">
                  <td className="py-3 px-4 text-white uppercase">Total</td>
                  {displayDays.map((day) => {
                    const dayStr = format(day, 'yyyy-MM-dd');
                    const total = report.employees.reduce((sum, emp) => {
                      return sum + (emp.daily_hours?.[dayStr] || 0);
                    }, 0);
                    return (
                      <td key={dayStr} className="text-center py-3 px-2 text-slate-300">
                        {total > 0 ? total.toFixed(1) : '-'}
                      </td>
                    );
                  })}
                  <td className="text-center py-3 px-4 text-amber-400 text-lg">
                    {report.total_hours.toFixed(1)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="card-industrial text-center py-12">
            <Clock className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No time entries for this period</p>
            <p className="text-slate-500 text-sm mt-1">
              Time entries will appear here when employees log their hours
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TimeReportPage;
